import { isDevProfileSwitcherEnabled } from "configs/DevAuth";
import classes from "./classes.module.scss";
import PageTemplateLogin from "components/PageTemplateLogin";
import WelcomeStep from "./WelcomeStep";
import { lazy, Suspense, useContext, useEffect, useState } from "react";
import { LoginFormContext, LoginFormProvider } from "contexts/LoginForm";
import ConnectStep from "./ConnectStep";
import ResetPasswordStep from "./ResetPasswordStep";
import RequestPinCodeStep from "./RequestPinCodeStep";
import * as P from "ts-pattern";
import { useSearchParams } from "react-router-dom";
import PinCodeLoginStep from "./PinCodeLoginStep";

const DevProfilePicker =
	process.env.NODE_ENV === "development" ? lazy(() => import("components/materials/DevProfileSwitcher").then((module) => ({ default: module.DevProfilePicker }))) : () => null;

enum EStep {
	LOADING = "LOADING",
	WELCOME = "WELCOME",
	CONNECT = "CONNECT",
	REQUEST_PIN_CODE = "REQUEST_PIN_CODE",
	RESET_PASSWORD = "RESET_PASSWORD",
	PIN_CODE_LOGIN = "PIN_CODE_LOGIN",
}

export default function Login() {
	const [passwordLogin, setPasswordLogin] = useState(false);
	if (isDevProfileSwitcherEnabled && !passwordLogin)
		return (
			<Suspense fallback={<p role="status">Chargement des profils…</p>}>
				<DevProfilePicker onPasswordLogin={() => setPasswordLogin(true)} />
			</Suspense>
		);
	return (
		<LoginFormProvider>
			<LoginContext />
		</LoginFormProvider>
	);
}

function LoginContext() {
	const loginFormContext = useContext(LoginFormContext);
	const [step, setStep] = useState<EStep>(EStep.LOADING);
	const [params] = useSearchParams();
	const emailSearchParams = params.get("email");
	const userEmail = emailSearchParams ? decodeURIComponent(emailSearchParams) : null;

	useEffect(() => {
		const isEmailExist = loginFormContext.formData?.email;

		if (!isEmailExist) return setStep(EStep.WELCOME);

		const hasUserForgottenPassword = loginFormContext.hasUserForgottenPassword;
		const isUserRegistered = !!loginFormContext.userRegisterInfo?.registeredAt;
		const hasPasswordPinCode = loginFormContext.formData?.passwordPinCode;

		if (loginFormContext.loginFormData?.email && loginFormContext.loginFormData?.password) {
			return setStep(EStep.PIN_CODE_LOGIN);
		}

		if (!isUserRegistered || hasUserForgottenPassword) {
			if (hasPasswordPinCode) return setStep(EStep.RESET_PASSWORD);
			return setStep(EStep.REQUEST_PIN_CODE);
		}

		return setStep(EStep.CONNECT);
	}, [
		loginFormContext.formData?.email,
		loginFormContext.formData?.passwordPinCode,
		loginFormContext.hasUserForgottenPassword,
		loginFormContext.userRegisterInfo?.registeredAt,
		loginFormContext.loginFormData?.email,
		loginFormContext.loginFormData?.password,
	]);

	return (
		<PageTemplateLogin>
			<div className={classes["root"]}>
				{P.match(step)
					.with(EStep.LOADING, () => null)
					.with(EStep.WELCOME, () => <WelcomeStep userEmail={userEmail} />)
					.with(EStep.RESET_PASSWORD, () => <ResetPasswordStep />)
					.with(EStep.REQUEST_PIN_CODE, () => <RequestPinCodeStep />)
					.with(EStep.CONNECT, () => <ConnectStep />)
					.with(EStep.PIN_CODE_LOGIN, () => <PinCodeLoginStep />)
					.exhaustive()}
			</div>
		</PageTemplateLogin>
	);
}
